import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, Tag as TagIcon, Calendar, BookOpen } from "lucide-react";
import { BlogPost } from "@/hooks/useBlogPosts";
import { TrendingArticles } from "./TrendingArticles";
import { RelatedDestinations } from "./RelatedDestinations";

interface BlogSidebarProps {
  posts: BlogPost[];
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
  onTagClick: (tag: string) => void;
  onArticleClick: (post: BlogPost) => void;
}

export const BlogSidebar = ({ 
  posts, 
  selectedCategory, 
  onCategorySelect,
  onTagClick,
  onArticleClick 
}: BlogSidebarProps) => {
  // Calculate tag frequencies
  const tagFrequency: Record<string, number> = {};
  posts.forEach(post => {
    post.tags.forEach(tag => {
      tagFrequency[tag] = (tagFrequency[tag] || 0) + 1;
    });
  });

  // Get top 10 most popular tags
  const popularTags = Object.entries(tagFrequency)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([tag, count]) => ({ tag, count }));

  // Get unique categories with post counts
  const categoryCount: Record<string, number> = {};
  posts.forEach(post => {
    categoryCount[post.category] = (categoryCount[post.category] || 0) + 1;
  });

  const categories = Object.entries(categoryCount)
    .sort(([, a], [, b]) => b - a);

  // Featured posts
  const featuredPosts = posts.filter(p => p.featured).slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Trending Articles */}
      <TrendingArticles onArticleClick={onArticleClick} />
      
      {/* Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="h-5 w-5" />
            Categories
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button
            variant={selectedCategory === 'All' ? 'default' : 'ghost'}
            className="w-full justify-start"
            onClick={() => onCategorySelect('All')}
          >
            <span className="flex-1 text-left">All Articles</span>
            <Badge variant="secondary">{posts.length}</Badge>
          </Button>
          {categories.map(([category, count]) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'ghost'}
              className="w-full justify-start"
              onClick={() => onCategorySelect(category)}
            >
              <span className="flex-1 text-left truncate">{category}</span>
              <Badge variant="secondary">{count}</Badge>
            </Button>
          ))}
        </CardContent>
      </Card>

      {/* Popular Tags */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5" />
            Trending Topics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {popularTags.map(({ tag, count }) => (
              <Badge
                key={tag}
                variant="outline"
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                onClick={() => onTagClick(tag)}
              >
                <TagIcon className="h-3 w-3 mr-1" />
                {tag}
                <span className="ml-1 text-xs opacity-70">({count})</span>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="h-5 w-5" />
              Featured Posts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {featuredPosts.map((post) => (
              <div
                key={post.id}
                className="group cursor-pointer"
                onClick={() => {
                  // This will be handled by parent component
                  const event = new CustomEvent('featured-post-click', { detail: post });
                  window.dispatchEvent(event);
                }}
              >
                <div className="aspect-video overflow-hidden rounded-lg mb-2">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h4 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                  {post.title}
                </h4>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {post.category}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {post.readTime}
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Related Destinations */}
      <RelatedDestinations 
        category={selectedCategory !== 'All' ? selectedCategory : undefined}
      />
    </div>
  );
};
